import type { ProductVariation } from '@zoomcatalog/design-templates'
import pako from 'pako'

type AssetForVirtual = {
	asset_id: string
	url: string
	type?: string
}

export const ProductCompressionService = {
	/**
	 * Compresses variations using GZIP and converts them to base64
	 */
	compressVariations(variations: ProductVariation[]): string {
		if (!variations || !Array.isArray(variations) || variations.length === 0) {
			return ''
		}

		try {
			const plainVariations = JSON.parse(JSON.stringify(variations))

			const optimizedVariations = plainVariations.map((variation: any) => {
				const { images: _images, ...rest } = variation
				return rest
			})

			const jsonString = JSON.stringify(optimizedVariations)
			const compressed = pako.deflate(jsonString)
			return this.arrayBufferToBase64(compressed)
		} catch (error) {
			console.error('Error compressing variations:', error)
			return ''
		}
	},

	/**
	 * Decompresses variations from base64 + GZIP
	 */
	decompressVariations(compressedString: string): ProductVariation[] {
		if (!compressedString || typeof compressedString !== 'string') {
			return []
		}

		try {
			const compressed = this.base64ToArrayBuffer(compressedString)
			const decompressed = pako.inflate(compressed, { to: 'string' })
			return JSON.parse(decompressed)
		} catch (error) {
			console.error('Error decompressing variations:', error)
			return []
		}
	},

	/**
	 * Compresses assets for virtuals using GZIP and converts them to base64
	 * Optimizes URLs before compressing to further reduce size
	 */
	compressAssetsForVirtuals(assets: AssetForVirtual[]): string {
		if (!assets || !Array.isArray(assets) || assets.length === 0) {
			return ''
		}

		try {
			const optimizedAssets = this.optimizeAssets(assets)
			const jsonString = JSON.stringify(optimizedAssets)
			const compressed = pako.deflate(jsonString)
			return this.arrayBufferToBase64(compressed)
		} catch (error) {
			console.error('Error compressing assets, falling back to uncompressed JSON:', error)
			// Fallback: return assets as uncompressed JSON to avoid data loss
			return JSON.stringify(assets)
		}
	},

	/**
	 * Optimizes assets by extracting the common base URL
	 */
	optimizeAssets(assets: AssetForVirtual[]): any {
		if (assets.length === 0) return { b: '', a: [] }

		// Find common prefix in URLs
		const urls = assets.map((a) => a.url)
		let baseUrl = urls[0]
		for (const url of urls) {
			while (!url.startsWith(baseUrl) && baseUrl.length > 0) {
				baseUrl = baseUrl.slice(0, -1)
			}
		}

		// Extract up to the last '/' of the common prefix
		const lastSlash = baseUrl.lastIndexOf('/')
		baseUrl = lastSlash > 0 ? baseUrl.substring(0, lastSlash + 1) : ''

		// Create optimized version
		return {
			b: baseUrl, // base URL
			a: assets.map((asset) => ({
				i: asset.asset_id,
				u: asset.url.replace(baseUrl, ''), // Relative URL
				t: asset.type,
			})),
		}
	},

	/**
	 * Decompresses assets for virtuals from base64 + GZIP
	 */
	decompressAssetsForVirtuals(compressedString: string): AssetForVirtual[] {
		if (!compressedString || typeof compressedString !== 'string') {
			return []
		}

		try {
			const compressed = this.base64ToArrayBuffer(compressedString)
			const decompressed = pako.inflate(compressed, { to: 'string' })
			const optimized = JSON.parse(decompressed)

			// Rebuild assets from optimized format
			if (optimized.b !== undefined && optimized.a !== undefined) {
				const baseUrl = optimized.b
				return optimized.a.map((asset: any) => ({
					asset_id: asset.i,
					url: baseUrl + asset.u,
					type: asset.t,
				}))
			}

			// Old format (not optimized)
			return optimized
		} catch (error) {
			console.error('Error decompressing assets:', error)
			return []
		}
	},

	/**
	 * Converts a Uint8Array to base64
	 */
	arrayBufferToBase64(buffer: Uint8Array): string {
		let binary = ''
		const bytes = new Uint8Array(buffer)
		const len = bytes.byteLength
		for (let i = 0; i < len; i++) {
			binary += String.fromCharCode(bytes[i])
		}
		return btoa(binary)
	},

	/**
	 * Converts base64 to Uint8Array
	 */
	base64ToArrayBuffer(base64: string): Uint8Array {
		const binaryString = atob(base64)
		const len = binaryString.length
		const bytes = new Uint8Array(len)
		for (let i = 0; i < len; i++) {
			bytes[i] = binaryString.charCodeAt(i)
		}
		return bytes
	},
}
